import React, { useContext, useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import styles from './Login.module.css';
import { AuthContext } from '../contexts/AuthContext';

export default function Login() {
    const { isAuthenticated, login, logout, user } = useContext(AuthContext);
    const [userName, setUserName] = useState('');
    const [password, setPassword] = useState('');
    const [mfaRequired, setMfaRequired] = useState(false);
    const [userId, setUserId] = useState('');
    const [mfaCode, setMfaCode] = useState('');
    const [rememberDevice, setRememberDevice] = useState(false);
    const [error, setError] = useState('');
    const [authMethods, setAuthMethods] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        async function fetchAuthMethods() {
            try {
                const response = await fetch('/api/account/authmethods', {
                    method: 'GET',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' }
                });
                if (response.ok) {
                    const data = await response.json();
                    setAuthMethods(data);
                } else {
                    setAuthMethods({
                        internalAuth: { enabled: true, label: "Internal (Embedded)" },
                        office365: { enabled: false, label: "Office 365" }
                    });
                }
            } catch (err) {
                console.error(err);
                setAuthMethods({
                    internalAuth: { enabled: true, label: "Internal (Embedded)" },
                    office365: { enabled: false, label: "Office 365" }
                });
            }
        }
        fetchAuthMethods();
    }, []);

    useEffect(() => {
        if (isAuthenticated && user?.mustChangePassword) {
            navigate('/changePassword');
        }
    }, [isAuthenticated, user, navigate]);

    // If already authenticated, redirect to home page immediately
    if (isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        try {
            const response = await fetch('/api/account/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ userName, password, rememberMe: true })
            });
            if (!response.ok) {
                const errorData = await response.json();
                let errorMsg = errorData.message || 'Login failed.';
                if (errorData.lockedOut && errorData.lockoutEnd) {
                    const lockoutEnd = new Date(errorData.lockoutEnd);
                    const now = new Date();
                    const diffMinutes = Math.ceil((lockoutEnd - now) / 60000);
                    errorMsg = `Your account is locked for the next ${diffMinutes} minute(s).`;
                } else if (errorData.attemptsRemaining !== undefined) {
                    errorMsg += ` ${errorData.attemptsRemaining} attempts remaining.`;
                }
                setError(errorMsg);
            } else {
                const result = await response.json();
                console.log("Login response:", result);
                if (result.requiresTwoFactor) {
                    setMfaRequired(true);
                    setUserId(result.userId);
                } else if (result.mustChangePassword) {
                    await login();
                    navigate('/changePassword');
                } else {
                    await login();
                    navigate('/');
                }
            }
        } catch (err) {
            console.error(err);
            setError('An error occurred during login.');
        }
    };

    const handleVerifyMfa = async (e) => {
        e.preventDefault();
        setError('');
        try {
            const response = await fetch('/api/account/verify2fa', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    userId,
                    token: mfaCode,
                    rememberMe: true,
                    rememberDevice: rememberDevice
                })
            });
            if (!response.ok) {
                const errorData = await response.json();
                setError(errorData.message || 'MFA verification failed.');
            } else {
                await login();
                navigate('/');
            }
        } catch (err) {
            console.error(err);
            setError('An error occurred during MFA verification.');
        }
    };

    const handleOffice365Login = () => {
        window.location.href = '/api/account/externalLogin?provider=AzureAD';
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    if (mfaRequired) {
        return (
            <div className={styles.container}>
                <h2 className={styles.header}>Enter Security Code</h2>
                {error && <div className={styles.error}>{error}</div>}
                <form onSubmit={handleVerifyMfa}>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Security Code:</label>
                        <input
                            type="text"
                            className={styles.input}
                            value={mfaCode}
                            onChange={(e) => setMfaCode(e.target.value)}
                            required
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>
                            <input
                                type="checkbox"
                                checked={rememberDevice}
                                onChange={(e) => setRememberDevice(e.target.checked)}
                            />
                            {' '}Remember this device for 30 days
                        </label>
                    </div>
                    <button type="submit" className={styles.button}>Verify Code</button>
                </form>
            </div>
        );
    }

    if (!authMethods) {
        return (
            <div className={styles.container}>
                <h2 className={styles.header}>Loading...</h2>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <h2 className={styles.header}>Login</h2>
            {error && <div className={styles.error}>{error}</div>}
            {authMethods.internalAuth.enabled && (
                <form onSubmit={handleLogin}>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Username:</label>
                        <input
                            type="text"
                            className={styles.input}
                            value={userName}
                            onChange={(e) => setUserName(e.target.value)}
                            autoComplete="username"
                            required
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>Password:</label>
                        <input
                            type="password"
                            className={styles.input}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="current-password"
                            required
                        />
                    </div>
                    <button type="submit" className={styles.button}>Log In</button>
                </form>
            )}
            {authMethods.office365.enabled && (
                <>
                    <hr />
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                        <button onClick={handleOffice365Login} className={styles.button}>
                            Log In with Office 365
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
